import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { AuditPlanListResult } from "@/modules/internal-audit/domain/types";
import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  DataTable,
  confirm,
  createSortableColumn,
  toast,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  CheckIcon,
  EditIcon,
  EyeIcon,
  FileTextIcon,
  TrashIcon,
} from "lucide-react";
import { useDeleteAuditPlan } from "../api/queries";

type AuditPlanRow = AuditPlanListResult["data"][number];

type AuditPlanTableProps = {
  data: AuditPlanRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (plan: AuditPlanRow) => void;
  onView: (plan: AuditPlanRow) => void;
  onSelect?: (plan: AuditPlanRow) => void;
  onCreateRecord?: (plan: AuditPlanRow) => void;
};

const statusLabels: Record<string, string> = {
  pending: "ຍັງບໍ່ກວດສອບ",
  in_progress: "ກຳລັງກວດສອບ",
  completed: "ກວດສອບແລ້ວ",
  cancelled: "ຍົກເລີກ",
};

const statusVariants: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "secondary",
  in_progress: "default",
  completed: "outline",
  cancelled: "destructive",
};

const frequencyLabels: Record<string, string> = {
  monthly: "ປະຈຳເດືອນ",
  quarterly: "ປະຈຳໄຕມາດ",
  semi_annual: "ຄັ້ງຕໍ່ 6 ເດືອນ",
  annual: "ປະຈຳປີ",
  ad_hoc: "ຕາມຄວາມຈຳເປັນ",
};

export function AuditPlanTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onView,
  onSelect,
  onCreateRecord,
}: AuditPlanTableProps) {
  const { has } = usePermissions();
  const deleteAuditPlan = useDeleteAuditPlan();

  const columns: TanstackReactTable.ColumnDef<AuditPlanRow>[] = [
    createSortableColumn<AuditPlanRow>("name", "ຊື່ຜະແນການກວດສອບ", {
      size: 200,
    }),
    createSortableColumn<AuditPlanRow>("createdAt", "ວັນທີສ້າງ", {
      size: 120,
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-muted-foreground text-xs">
          {row.original.createdAt
            ? formatDateTime(row.original.createdAt)
            : "-"}
        </div>
      ),
    }),
    createSortableColumn<AuditPlanRow>("auditTypeName", "ປະເພດການກວດສອບ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-sm">{row.original.auditTypeName || "-"}</span>
      ),
    }),
    createSortableColumn<AuditPlanRow>("departmentName", "ພະແນກ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
    }),
    createSortableColumn<AuditPlanRow>("frequency", "ຄວາມຖີ່", {
      size: 120,
      cell: ({ row }) => (
        <Badge variant="outline" className="text-xs">
          {frequencyLabels[row.original.frequency] || row.original.frequency}
        </Badge>
      ),
    }),
    createSortableColumn<AuditPlanRow>("auditorPositionTitle", "ຜູ້ກວດສອບ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.auditorPositionTitle || "-"}
        </span>
      ),
    }),
    createSortableColumn<AuditPlanRow>("nextAuditDate", "ວັນທີກວດສອບຄັ້ງຖັດ", {
      size: 150,
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-muted-foreground text-xs">
          {row.original.nextAuditDate
            ? formatDate(row.original.nextAuditDate)
            : "-"}
        </div>
      ),
    }),
    createSortableColumn<AuditPlanRow>("status", "ສະຖານະ", {
      size: 120,
      cell: ({ row }) => (
        <Badge variant={statusVariants[row.original.status] || "secondary"}>
          {statusLabels[row.original.status] || row.original.status}
        </Badge>
      ),
    }),
    createSortableColumn<AuditPlanRow>("updatedAt", "ວັນທີອັບເດດ", {
      size: 120,
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-muted-foreground text-xs">
          {row.original.updatedAt
            ? formatDateTime(row.original.updatedAt)
            : "-"}
        </div>
      ),
    }),
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: AuditPlanRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (onSelect) {
          actions.push({
            label: "ເລືອກ",
            icon: <CheckIcon className="h-4 w-4" />,
            onClick: () => onSelect(row.original),
          });
        }

        if (has("internal_audit:read"))
          actions.push({
            label: "ເບິ່ງ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original),
          });

        if (onCreateRecord && has("internal_audit:create"))
          actions.push({
            label: "ບັນທຶກການກວດສອບ",
            icon: <FileTextIcon className="h-4 w-4" />,
            onClick: () => onCreateRecord(row.original),
          });

        if (has("internal_audit:update"))
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });

        if (has("internal_audit:delete"))
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const plan = row.original;
              const ok = await confirm({
                title: "ລຶບຜະແນການກວດສອບ",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຜະແນການກວດສອບ "${plan.name}"? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`,
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                toast.promise(deleteAuditPlan.mutateAsync(plan.id), {
                  loading: "ກຳລັງລຶບຜະແນການກວດສອບ...",
                  success: "ລຶບຜະແນການກວດສອບສຳເລັດ",
                  error: (err: Error) => err.message || "ລຶບຜະແນການກວດສອບລົ້ມເຫຼວ",
                });
              }
            },
          });

        return <RowActions actions={actions} />;
      },
    },
  ];

  return (
    <div data-tourid="plan-table">
      <DataTable<AuditPlanRow, unknown>
        noDataMessage="ບໍ່ພົບຜະແນການກວດສອບ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (!sorting[0] || !sorting[0].id || sorting[0].id === "") return;
          onSortingChange(sorting[0].id as string, !!sorting[0].desc);
        }}
      />
    </div>
  );
}
